import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ME_QUERY_KEY, type MeResponse } from "@/lib/auth";
import { useListaFiltrada } from "@/lib/filtros";
import { type FiltrosUsuarios, filtrarUsuarios } from "@/lib/filtros/usuarios";
import {
  type AutorizacionObra,
  type DocumentoAcreditativo,
  descargarDocumento,
  formularioDocumento,
  invalidarTrasDocumento,
  type RolMiembro,
  type SubirDocumentoVars,
} from "@/lib/obras";

/**
 * PersonaFields es el sub-payload de datos personales (compartido por alta/edición).
 * Los campos opcionales viajan como null cuando están vacíos (el backend los
 * normaliza con trimOpcional).
 */
export type PersonaFields = {
  nombre: string;
  apellidos: string;
  dni_o_matricula: string | null;
  email: string | null;
  cargo: string | null;
  area: string | null;
  activo: boolean;
};

/**
 * UsuarioCreateInput es el payload de alta combinada persona + usuario. F7 (#153):
 * la cuenta es GLOBAL y SIN rol; el rol es por obra (POST /obras/{id}/miembros) y la
 * autorización LOTO por obra (POST /obras/{id}/autorizaciones). `es_admin` es el flag
 * de superadministrador global. La identidad Microsoft (entra_upn) ya no se fija a
 * mano: se auto-vincula por email en el login SSO (#190). Espejo de
 * usermgmt.UsuarioCreateInput.
 */
export type UsuarioCreateInput = {
  persona: PersonaFields;
  nombre_usuario: string;
  es_admin: boolean;
  activo: boolean;
};

/**
 * UsuarioUpdateInput es el payload de edición. No lleva contraseña: la fija su titular al
 * aceptar la invitación (#239) o al recuperarla él mismo. Como el alta, es global, sin rol
 * y sin identidad Microsoft (auto-vínculo por SSO, #190).
 */
export type UsuarioUpdateInput = {
  persona: PersonaFields;
  nombre_usuario: string;
  es_admin: boolean;
  activo: boolean;
};

export type PersonaResponse = {
  id: number;
  nombre: string;
  apellidos: string;
  dni_o_matricula?: string | null;
  email?: string | null;
  cargo?: string | null;
  area?: string | null;
  activo: boolean;
};

/**
 * UsuarioObra es la pertenencia de un usuario a una obra tal como la sirve su ficha
 * (#238): la obra identificada, su rol en ella, su autorización LOTO en esa obra (o
 * `null`) y la regla `puede_registrar` ya resuelta por el servidor. Espejo de
 * usermgmt.UsuarioObraResponse.
 */
export type UsuarioObra = {
  id_obra: number;
  codigo_obra: string;
  nombre: string;
  rol: RolMiembro;
  autorizacion: AutorizacionObra | null;
  puede_registrar: boolean;
};

/**
 * AutorizacionObraInput son los campos de la autorización LOTO que viajan junto a la
 * pertenencia (PUT /usuarios/{id}/obras/{obraID}): los mismos que AutorizacionInput sin
 * `id_persona`, que el servidor resuelve desde el usuario de la ruta.
 */
export type AutorizacionObraInput = {
  autorizado_loto: boolean;
  vigente: boolean;
  fecha_autorizacion: string | null;
  fecha_caducidad: string | null;
  emitida_por: string | null;
  alcance: string | null;
  observaciones: string | null;
};

/**
 * UsuarioObraInput es el payload de PUT /usuarios/{id}/obras/{obraID}: el rol en la obra
 * y, opcionalmente, la autorización LOTO (ambos en una sola transacción del servidor).
 */
export type UsuarioObraInput = {
  rol: RolMiembro;
  autorizacion?: AutorizacionObraInput | null;
};

/**
 * Usuario es la representación de un usuario para el SPA: cuenta GLOBAL + persona +
 * flag `es_admin` (superadministrador global). F7 (#153): el rol y la autorización LOTO
 * son POR obra; desde #238 viajan en `obras[]` (siempre lista, nunca null) porque la ficha
 * del usuario es el sitio único donde el ADMIN los ve y los edita.
 * GET /api/v1/usuarios. Espejo de usermgmt.UsuarioResponse.
 */
export type Usuario = {
  id: number;
  nombre_usuario: string;
  es_admin: boolean;
  entra_upn?: string | null;
  activo: boolean;
  debe_cambiar_password: boolean;
  ultimo_acceso?: string | null;
  persona: PersonaResponse;
  obras: UsuarioObra[];
  /**
   * Estado del USUARIO (#239 E1): la cuenta existe pero aún no tiene acceso. No coincide
   * con `invitacion`: tras revocar, el usuario sigue pendiente y sin invitación viva.
   */
  invitacion_pendiente: boolean;
  /** Invitación VIVA (#239); null cuando ya aceptó, se revocó o nunca se invitó. */
  invitacion: InvitacionUsuario | null;
  /** Documentación acreditativa de la persona (#435); null si no la ha aportado. */
  documento_acreditativo?: DocumentoAcreditativo | null;
  version: number;
  updated_at: string;
};

/**
 * InvitacionUsuario es el estado de la invitación VIVA de un usuario (#239, D12), o null
 * si no la tiene: aceptada, revocada sin reemplazo o nunca invitada. Nunca lleva el token:
 * el enlace solo existe en el correo. Espejo de usermgmt.InvitacionResponse.
 */
export type InvitacionUsuario = {
  estado: "pendiente" | "caducada";
  enviada_en: string;
  expira_en: string;
};

/** CorreoUsuariosEstado dice si la instancia puede invitar (SMTP + URL base, #239 D10). */
export type CorreoUsuariosEstado = {
  configurado: boolean;
};

/** InvitarResult confirma la emisión de una invitación: solo cuándo caduca. */
export type InvitarResult = {
  expira_en: string;
};

export const USUARIOS_QUERY_KEY = ["usuarios"] as const;

/**
 * usuariosQueryOptions lista todos los usuarios (activos e inactivos). Se refresca
 * invalidando la clave tras cada mutación, igual que los maestros. El slice es
 * ADMIN-only en el backend (RequireAdmin); la UI oculta además el acceso.
 */
export const usuariosQueryOptions = queryOptions({
  queryKey: USUARIOS_QUERY_KEY,
  queryFn: () => api.get<Usuario[]>("/usuarios"),
});

/**
 * useUsuarios lista los usuarios, opcionalmente FILTRADOS (#247). Aquí el modo sin red no
 * llega a usarse —el listado global NO se persiste offline, la allowlist de la caché lo
 * excluye a propósito—, pero la función pura sigue siendo la fuente de la equivalencia con
 * el SQL (D2/R7). La clave filtrada es `["usuarios", filtros]`, tampoco persistida.
 */
export function useUsuarios(filtros: FiltrosUsuarios = {}) {
  return useListaFiltrada<Usuario>({
    claveBase: USUARIOS_QUERY_KEY,
    url: "/usuarios",
    filtros,
    filtrar: filtrarUsuarios,
  });
}

export function useCreateUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UsuarioCreateInput) => api.post<Usuario>("/usuarios", input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_QUERY_KEY }),
  });
}

export function useUpdateUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UsuarioUpdateInput }) =>
      api.put<Usuario>(`/usuarios/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_QUERY_KEY }),
  });
}

export function useToggleActivoUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      api.patch<Usuario>(`/usuarios/${id}/activo`, { activo }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_QUERY_KEY }),
  });
}

// --- Invitaciones de registro (#239, ADMIN global) ---

// CORREO_USUARIOS_QUERY_KEY es su propia raíz (no cuelga de ["usuarios"]) para que
// invalidar el listado tras cada mutación no re-consulte además el estado del SMTP, que
// es de entorno y no cambia en caliente.
export const CORREO_USUARIOS_QUERY_KEY = ["usuarios-correo"] as const;

/**
 * useCorreoUsuarios consulta si la instancia puede invitar (GET /usuarios/correo). La
 * pantalla lo usa para avisar y deshabilitar «Nuevo usuario» antes de intentarlo.
 */
export function useCorreoUsuarios() {
  return useQuery({
    queryKey: CORREO_USUARIOS_QUERY_KEY,
    queryFn: () => api.get<CorreoUsuariosEstado>("/usuarios/correo"),
    retry: false,
  });
}

/** useInvitar reenvía la invitación de un usuario pendiente (revoca la viva y emite otra). */
export function useInvitar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<InvitarResult>(`/usuarios/${id}/invitar`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_QUERY_KEY }),
  });
}

/** useRevocarInvitacion retira la invitación viva; el usuario sigue pendiente y sin acceso. */
export function useRevocarInvitacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/usuarios/${id}/invitacion`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_QUERY_KEY }),
  });
}

// --- Pertenencia a obras desde la ficha del usuario (#238, ADMIN global) ---

/** SetUsuarioObraVars identifica la pertenencia a escribir y su contenido. */
export type SetUsuarioObraVars = {
  idUsuario: number;
  obraId: number;
  input: UsuarioObraInput;
};

/** QuitarUsuarioObraVars identifica la pertenencia a retirar. */
export type QuitarUsuarioObraVars = { idUsuario: number; obraId: number };

/**
 * invalidarPertenencia refresca lo que depende de la pertenencia a obras: el listado de
 * usuarios y, SOLO si el usuario editado es el propio, el `/me` — de él cuelgan el menú, el
 * conmutador de obra y los gates de la SPA, así que cambiarse el rol a uno mismo debe verse
 * sin recargar. Cambiárselo a otro no afecta a la sesión de quien edita.
 */
function invalidarPertenencia(
  queryClient: ReturnType<typeof useQueryClient>,
  idUsuario: number,
): void {
  void queryClient.invalidateQueries({ queryKey: USUARIOS_QUERY_KEY });
  const me = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY);
  if (me?.id === idUsuario) {
    void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }
}

/**
 * useSetUsuarioObra asigna (o cambia) el rol del usuario en una obra y, si viene, su
 * autorización LOTO, con PUT /usuarios/{id}/obras/{obraId} (el servidor escribe ambas en
 * una sola transacción). Ruta GLOBAL ADMIN-only; la UI oculta además el acceso.
 */
export function useSetUsuarioObra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idUsuario, obraId, input }: SetUsuarioObraVars) =>
      api.put<UsuarioObra>(`/usuarios/${idUsuario}/obras/${obraId}`, input),
    onSuccess: (_data, { idUsuario }) => invalidarPertenencia(queryClient, idUsuario),
  });
}

/**
 * useQuitarUsuarioObra retira la pertenencia del usuario a una obra con DELETE
 * /usuarios/{id}/obras/{obraId}. El servidor CONSERVA su autorización LOTO en esa obra.
 */
export function useQuitarUsuarioObra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idUsuario, obraId }: QuitarUsuarioObraVars) =>
      api.del<void>(`/usuarios/${idUsuario}/obras/${obraId}`),
    onSuccess: (_data, { idUsuario }) => invalidarPertenencia(queryClient, idUsuario),
  });
}

// --- Documentación acreditativa desde la ficha del usuario (#435, ADMIN global) ---

function rutaDocumentoUsuario(idUsuario: number): string {
  return `/usuarios/${idUsuario}/documento-acreditativo`;
}

/**
 * useSubirDocumentoUsuario sube (o sustituye) la documentación acreditativa de un usuario con
 * PUT multipart a la ruta GLOBAL de ADMIN. Invalida usuarios, miembros de toda obra y `me`.
 */
export function useSubirDocumentoUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idUsuario, archivo }: SubirDocumentoVars) =>
      api.putForm<DocumentoAcreditativo>(
        rutaDocumentoUsuario(idUsuario),
        formularioDocumento(archivo),
      ),
    onSuccess: () => invalidarTrasDocumento(queryClient),
  });
}

/** useBorrarDocumentoUsuario borra la documentación acreditativa de un usuario. */
export function useBorrarDocumentoUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idUsuario: number) => api.del<void>(rutaDocumentoUsuario(idUsuario)),
    onSuccess: () => invalidarTrasDocumento(queryClient),
  });
}

/** useDescargarDocumentoUsuario descarga la documentación acreditativa de un usuario. */
export function useDescargarDocumentoUsuario() {
  return useMutation({
    mutationFn: (idUsuario: number) => descargarDocumento(rutaDocumentoUsuario(idUsuario)),
  });
}
